import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { marked } from 'marked';
import { ArrowLeft } from 'lucide-react';

const ALLOWED_SLUGS = ['pogoji', 'zasebnost', 'dpa'] as const;
type Slug = typeof ALLOWED_SLUGS[number];

function isValidSlug(s: string): s is Slug {
  return (ALLOWED_SLUGS as readonly string[]).includes(s);
}

export const Route = createFileRoute('/pravno/$slug')({
  component: LegalPage,
});

function LegalPage() {
  const { slug } = Route.useParams();
  const [html, setHtml] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isValidSlug(slug)) {
      setError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    fetch(`/legal/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((md) => {
        const parsed = marked.parse(md);
        setHtml(typeof parsed === 'string' ? parsed : '');
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  return (
    <div className="min-h-dvh bg-parchment">
      <header className="border-b border-linen bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/onboarding/identity" className="inline-flex items-center gap-1.5 text-sm text-steel hover:text-iron transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Nazaj
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {loading && (
          <div className="text-steel text-sm animate-pulse">Nalagam…</div>
        )}

        {error && (
          <div className="rounded-lg border border-linen bg-white p-8 text-center text-steel text-sm">
            Dokumenta ni mogoče naložiti.
          </div>
        )}

        {!loading && !error && (
          <div
            className="prose prose-stone max-w-none
              prose-headings:font-display prose-headings:text-forge
              prose-h1:text-display-md prose-h2:text-display-sm
              prose-a:text-copper prose-a:underline prose-a:underline-offset-2
              prose-strong:text-forge
              prose-blockquote:border-copper prose-blockquote:text-steel"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </main>
    </div>
  );
}
